import { DomainSearchError } from '../types';
import { CanConnectOrBindSuiteErrors, CanPurchaseDomainSuiteErrors, DomainNameErrors, DomainsPermissions, ManagementType, parseDomain } from '@wix/domains-serverless-interface';
import { getPremiumEssentialsSafe } from '@wix/premium-fed-sdk/@premium-essentials';
import { ExperimentSpecs } from '../../../constants';
import { extractResult } from '../utils';
/**
 * Configuration for action prioritization and error handling rules
 *
 * Priority: Lower numbers = higher priority (1 is highest priority)
 * Error Rules:
 * - 'always': Always throw error if action fails
 * - 'never': Never throw error, action can fail silently
 * - 'conditional': Throw error only if dependency actions didn't fail
 */
export const ACTION_CONFIG = _ref => {
  let {
    isSiteLevel
  } = _ref;
  return {
    assign: {
      priority: 10,
      errorEnforceRule: 'never',
      businessConditions: () => true,
      dependencies: {
        upgrade: {
          errorEnforceRule: 'always'
        }
      },
      businessErrorConditions: [CanConnectOrBindSuiteErrors.SUBDOMAIN_ALREADY_ASSIGNED, CanConnectOrBindSuiteErrors.ALREADY_ASSIGNED, CanConnectOrBindSuiteErrors.ALREADY_ASSIGNED_TO_THIS_SITE, CanConnectOrBindSuiteErrors.FLOATING_DOMAIN, CanConnectOrBindSuiteErrors.CANNOT_ASSIGN_TO_SHARED_SITE]
    },
    purchase: {
      priority: 20,
      errorEnforceRule: 'always',
      // If purchase fails, throw error
      businessConditions: () => true,
      businessErrorConditions: [CanPurchaseDomainSuiteErrors.NOT_SUPPORTED_TLD, CanPurchaseDomainSuiteErrors.NOT_SUPPORTED_TLD_REGIONAL],
      dependencies: {
        upgrade: {
          errorEnforceRule: 'always'
        }
      }
    },
    transfer: {
      priority: 30,
      errorEnforceRule: 'conditional',
      // Transfer can fail without throwing error
      businessConditions: parsedDomain => !parsedDomain.subdomain,
      businessErrorConditions: [],
      dependencies: {
        purchase: {
          errorEnforceRule: 'always'
        },
        upgrade: {
          errorEnforceRule: 'always'
        }
      }
    },
    connect: {
      priority: 40,
      errorEnforceRule: 'conditional',
      // Connect throws error only if purchase and upgrade didn't fail
      businessConditions: () => true,
      businessErrorConditions: [CanConnectOrBindSuiteErrors.DOMAIN_TAKEN_IN_WIX, CanConnectOrBindSuiteErrors.ROOT_DOMAIN_IS_CONNECTED_BY_POINTING, CanConnectOrBindSuiteErrors.SUBDOMAIN_ROOT_IS_REGISTERED, CanConnectOrBindSuiteErrors.SUBDOMAIN_ROOT_IS_CONNECTED_BY_DNS, CanConnectOrBindSuiteErrors.UNAUTHORIZED],
      dependencies: {
        purchase: {
          errorEnforceRule: 'always'
        },
        upgrade: {
          errorEnforceRule: 'always'
        }
      }
    },
    upgrade: {
      priority: 50,
      errorEnforceRule: isSiteLevel ? 'always' : 'never',
      // If upgrade fails, throw error
      businessConditions: () => isSiteLevel,
      dependencies: {}
    }
  };
};
export const capabilityConfigs = _ref2 => {
  var _actionConfigs$assign, _actionConfigs$purcha, _actionConfigs$connec;
  let {
    actionConfigs,
    connectOrBindResult,
    purchaseResult,
    transferResult,
    accountParamsResult
  } = _ref2;
  const purchase = extractResult(purchaseResult);
  const connectOrBind = extractResult(connectOrBindResult);
  const transfer = extractResult(transferResult);
  const accountParams = extractResult(accountParamsResult);
  const assignCapability = Boolean((connectOrBind == null ? void 0 : connectOrBind.canConnectOrBind) && (connectOrBind == null ? void 0 : connectOrBind.error) && ((_actionConfigs$assign = actionConfigs.assign) == null || (_actionConfigs$assign = _actionConfigs$assign.businessErrorConditions) == null ? void 0 : _actionConfigs$assign.includes(connectOrBind.error)));
  const transferCapability = Boolean(transfer == null ? void 0 : transfer.canTransferIn) && Boolean(!(connectOrBind != null && connectOrBind.error && [CanConnectOrBindSuiteErrors.DOMAIN_TAKEN_IN_WIX].includes(connectOrBind == null ? void 0 : connectOrBind.error)));
  const purchaseCapability = Boolean(purchase == null ? void 0 : purchase.canPurchase) || Boolean((purchase == null ? void 0 : purchase.error) && ((_actionConfigs$purcha = actionConfigs.purchase) == null || (_actionConfigs$purcha = _actionConfigs$purcha.businessErrorConditions) == null ? void 0 : _actionConfigs$purcha.includes(purchase == null ? void 0 : purchase.error)));
  const connectCapability = Boolean(connectOrBind == null ? void 0 : connectOrBind.canConnectOrBind) || Boolean((connectOrBind == null ? void 0 : connectOrBind.error) && ((_actionConfigs$connec = actionConfigs.connect) == null || (_actionConfigs$connec = _actionConfigs$connec.businessErrorConditions) == null ? void 0 : _actionConfigs$connec.includes(connectOrBind.error)));
  return {
    purchase: {
      result: purchase,
      promiseResult: purchaseResult,
      capability: purchaseCapability
    },
    connect: {
      result: connectOrBind,
      promiseResult: connectOrBindResult,
      capability: connectCapability
    },
    assign: {
      result: connectOrBind,
      promiseResult: connectOrBindResult,
      capability: assignCapability
    },
    transfer: {
      result: transfer,
      promiseResult: transferResult,
      // If the transfer is pending, locked, or not allowed, we will still return true in order for the error config to be able to handle the error.
      capability: transferCapability
    },
    upgrade: {
      result: extractResult(accountParamsResult),
      promiseResult: accountParamsResult,
      capability: Boolean(!(accountParams != null && accountParams.isPremiumSite))
    }
  };
};
export const actionErrorConfigs = _ref3 => {
  var _accountParams$domain2, _getPremiumEssentials, _accountParams$sites;
  let {
    configs,
    accountParams,
    domain,
    externalViewer
  } = _ref3;
  const parsedDomain = parseDomain(domain);
  const {
    connect,
    purchase
  } = configs;
  const connectOrBind = connect == null ? void 0 : connect.result;
  const purchaseResult = purchase == null ? void 0 : purchase.result;
  const hasPermissionsToConnect = Object.values(DomainsPermissions).some(permission => {
    var _accountParams$domain;
    return accountParams == null || (_accountParams$domain = accountParams.domainPermissions) == null ? void 0 : _accountParams$domain.includes(permission);
  });
  const pointingAssignDomainStatus = accountParams == null || (_accountParams$domain2 = accountParams.domains) == null || (_accountParams$domain2 = _accountParams$domain2.find(_ref4 => {
    let {
      domainName
    } = _ref4;
    return parseDomain(domainName ?? '').main === parsedDomain.main;
  })) == null ? void 0 : _accountParams$domain2.managementType;
  const ccPrefix = [CanConnectOrBindSuiteErrors.LANGUAGE_CODE_SUBDOMAIN].includes(connectOrBind == null ? void 0 : connectOrBind.error);
  const isSiteLevel = Boolean(accountParams == null ? void 0 : accountParams.msid);

  // Under the logged-in mobile connect experiment, zero-site users also get the
  // connect-upgrade flow despite having no site-level context.
  const isLoggedInMobileConnectOn = Boolean((_getPremiumEssentials = getPremiumEssentialsSafe()) == null || (_getPremiumEssentials = _getPremiumEssentials.experiments) == null ? void 0 : _getPremiumEssentials[ExperimentSpecs.LoggedInMobileConnect]);
  const hasZeroSites = ((accountParams == null || (_accountParams$sites = accountParams.sites) == null ? void 0 : _accountParams$sites.length) ?? 0) === 0;
  const shouldConnectUpgrade = isSiteLevel || isLoggedInMobileConnectOn && hasZeroSites;
  return {
    domainNameErrors: [DomainNameErrors.DOMAIN_TOO_LONG, DomainNameErrors.DOMAIN_TOO_SHORT, DomainNameErrors.OFAC_TLD, DomainNameErrors.LABEL_INVALID_CHARS, DomainNameErrors.INVALID_DOMAIN_NAME].includes(parsedDomain.error) && parsedDomain.error,
    assign: [{
      error: DomainSearchError.EXTERNAL_VIEWER_ASSIGN_SUBDOMAIN_NOT_ALLOWED,
      value: !!parsedDomain.subdomain && !!externalViewer
    }, {
      error: DomainSearchError.EXTERNAL_VIEWER_POINTING_DOMAIN,
      value: externalViewer && pointingAssignDomainStatus === ManagementType.POINTING
    }, {
      error: DomainSearchError.LANGUAGE_CODE_SUBDOMAIN,
      value: ccPrefix
    }, {
      error: DomainSearchError.CANNOT_ASSIGN_TO_SHARED_SITE,
      value: [CanConnectOrBindSuiteErrors.CANNOT_ASSIGN_TO_SHARED_SITE].includes(connectOrBind == null ? void 0 : connectOrBind.error)
    }, {
      error: DomainSearchError.UPGRADE_REQUIRE_ASSIGN,
      value: !(accountParams != null && accountParams.isPremiumSite) && isSiteLevel
    }, {
      error: DomainSearchError.FLOATING_DOMAIN,
      value: [CanConnectOrBindSuiteErrors.FLOATING_DOMAIN].includes(connectOrBind == null ? void 0 : connectOrBind.error)
    }, {
      error: DomainSearchError.ALREADY_ASSIGNED_TO_THIS_SITE,
      value: [CanConnectOrBindSuiteErrors.ALREADY_ASSIGNED_TO_THIS_SITE, CanConnectOrBindSuiteErrors.SUBDOMAIN_ALREADY_ASSIGNED].includes(connectOrBind == null ? void 0 : connectOrBind.error)
    }, {
      error: DomainSearchError.ALREADY_ASSIGNED,
      value: [CanConnectOrBindSuiteErrors.ALREADY_ASSIGNED].includes(connectOrBind == null ? void 0 : connectOrBind.error)
    }],
    purchase: [{
      error: DomainSearchError.EXTERNAL_VIEWER_PURCHASE_SUBDOMAIN_NOT_ALLOWED,
      value: !!parsedDomain.subdomain && !!externalViewer
    }, {
      error: DomainSearchError.NOT_SUPPORTED_TLD,
      value: [CanPurchaseDomainSuiteErrors.NOT_SUPPORTED_TLD].includes(purchaseResult == null ? void 0 : purchaseResult.error)
    }, {
      error: DomainSearchError.NOT_SUPPORTED_TLD_REGIONAL,
      value: [CanPurchaseDomainSuiteErrors.NOT_SUPPORTED_TLD_REGIONAL].includes(purchaseResult == null ? void 0 : purchaseResult.error)
    }, {
      error: DomainSearchError.UPGRADE_REQUIRE_PURCHASE_SUBDOMAIN,
      value: !!parsedDomain.subdomain && !(accountParams != null && accountParams.isPremiumSite) && isSiteLevel
    }, {
      error: DomainSearchError.UPGRADE_REQUIRE_PURCHASE,
      value: !(accountParams != null && accountParams.isPremiumSite) && isSiteLevel
    }, {
      error: DomainSearchError.PURCHASE_ROOT_DOMAIN_IS_REQUIRED,
      value: !!parsedDomain.subdomain
    }],
    connect: [{
      error: DomainSearchError.EXTERNAL_VIEWER_CONNECT_SUBDOMAIN_NOT_ALLOWED,
      value: !!parsedDomain.subdomain && !!externalViewer
    }, {
      error: DomainSearchError.LANGUAGE_CODE_SUBDOMAIN,
      value: ccPrefix
    }, {
      error: DomainSearchError.SUBDOMAIN_TAKEN_IN_WIX,
      value: Boolean(parsedDomain.subdomain && [CanConnectOrBindSuiteErrors.DOMAIN_TAKEN_IN_WIX].includes(connectOrBind == null ? void 0 : connectOrBind.error))
    }, {
      error: DomainSearchError.DOMAIN_TAKEN_IN_WIX,
      value: [CanConnectOrBindSuiteErrors.DOMAIN_TAKEN_IN_WIX].includes(connectOrBind == null ? void 0 : connectOrBind.error)
    }, {
      error: DomainSearchError.UPGRADE_REQUIRE_CONNECT,
      value: !(accountParams != null && accountParams.isPremiumSite) && shouldConnectUpgrade
    }, {
      error: DomainSearchError.NO_PERMISSIONS_TO_CONNECT,
      value: !hasPermissionsToConnect
    }, {
      error: DomainSearchError.ROOT_DOMAIN_IS_CONNECTED_BY_POINTING,
      value: [CanConnectOrBindSuiteErrors.ROOT_DOMAIN_IS_CONNECTED_BY_POINTING].includes(connectOrBind == null ? void 0 : connectOrBind.error)
    }, {
      error: DomainSearchError.SUBDOMAIN_ROOT_IS_CONNECTED_BY_DNS,
      value: [CanConnectOrBindSuiteErrors.SUBDOMAIN_ROOT_IS_CONNECTED_BY_DNS].includes(connectOrBind == null ? void 0 : connectOrBind.error)
    }, {
      error: DomainSearchError.SUBDOMAIN_ROOT_IS_REGISTERED,
      value: [CanConnectOrBindSuiteErrors.SUBDOMAIN_ROOT_IS_REGISTERED].includes(connectOrBind == null ? void 0 : connectOrBind.error)
    }, {
      error: DomainSearchError.SUBDOMAIN_ROOT_IS_NOT_CONNECTED,
      value: !!parsedDomain.subdomain
    }],
    transfer: [{
      error: DomainSearchError.UPGRADE_REQUIRE_TRANSFER_IN,
      value: !(accountParams != null && accountParams.isPremiumSite) && isSiteLevel
    }],
    TECHNICAL_ERROR: false
    // TODO: Need to add technical error - needs to define what exactly is a technical error
  };
};
export const getErrorStatus = _ref5 => {
  let {
    configs,
    accountParams,
    domain,
    externalViewer,
    status
  } = _ref5;
  const errorConfigs = actionErrorConfigs({
    configs,
    accountParams,
    domain,
    externalViewer
  });
  if (errorConfigs.domainNameErrors) {
    return errorConfigs.domainNameErrors;
  }

  // Get errors for the specific action
  const actionErrors = errorConfigs[status];
  if (!Array.isArray(actionErrors)) {
    return actionErrors ? actionErrors : undefined;
  }
  const errorArray = actionErrors;
  const foundError = errorArray.find(item => item.value);
  return foundError ? foundError.error : undefined;
};
//# sourceMappingURL=actionConfig.js.map